
import { useMemo } from 'react';
import { EventObject } from '../types';
import { startOfDay, endOfDay } from 'date-fns';

export const useProcessedEvents = (
	events: EventObject[],
	weekDays: Date[],
	startHour: number,
	endHour: number
) => {
	return useMemo(() => {
		const eventsByDay: Record<string, Array<EventObject & { position: number; isVisible: boolean }>> = {};
		
		weekDays.forEach((day) => {
			const dateKey = day.toISOString().split('T')[0];
			eventsByDay[dateKey] = [];
		});
		
		events.forEach(event => {
			weekDays.forEach(day => {
				const dayStart = startOfDay(day);
				const dayEnd = endOfDay(day);
				
				if (event.end > dayStart && event.start < dayEnd) {
					const dateKey = day.toISOString().split('T')[0];
					
					const clampedStart = event.start < dayStart ? dayStart : event.start;
					const clampedEnd = event.end > dayEnd ? dayEnd : event.end;
					
					const dayStartTime = new Date(day);
					dayStartTime.setHours(startHour, 0, 0, 0);
					
					const dayEndTime = new Date(day);
					dayEndTime.setHours(endHour + 1, 0, 0, 0);
					
					const isVisible = clampedEnd > dayStartTime && clampedStart < dayEndTime;
					
					if (isVisible) {
						if (!eventsByDay[dateKey]) {
							eventsByDay[dateKey] = [];
						}
						
						eventsByDay[dateKey].push({
							...event,
							start: clampedStart,
							end: clampedEnd,
							position: 0,
							isVisible
						});
					}
				}
			});
		});
		
		Object.keys(eventsByDay).forEach(dateKey => {
			const dayEvents = eventsByDay[dateKey];
			
			dayEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
			
			const positionTracker: { end: number, position: number }[] = [];
			
			dayEvents.forEach(event => {
				const eventStartTime = event.start.getTime();
				let position = 0;
				let foundPosition = false;
				
				while (!foundPosition) {
					const overlapsAtCurrentPosition = positionTracker
						.filter(tracker => tracker.position === position)
						.some(tracker => tracker.end > eventStartTime);
					
					if (overlapsAtCurrentPosition) {
						position++;
					} else {
						foundPosition = true;
					}
				}
				
				event.position = position;
				
				positionTracker.push({
					end: event.end.getTime(),
					position
				});
			});
		});
		
		return eventsByDay;
	}, [events, weekDays, startHour, endHour]);
};
